/**
 * SubmissionViewerModal — read-only view of a completed signing submission.
 * Shows PDF with all field values (signer + sender) overlaid at their positions.
 */
import React, { useState, useEffect } from 'react';
import { X, Loader2, Download } from 'lucide-react';
import { esignAPI } from '@/lib/api';
import { toast } from 'sonner';

function loadPdfJs() {
  return new Promise((resolve) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    document.head.appendChild(s);
  });
}

export default function SubmissionViewerModal({ submissionId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [pages, setPages] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    esignAPI.viewSubmission(submissionId)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { toast.error('Failed to load document'); onClose(); });
  }, [submissionId]);

  useEffect(() => {
    if (!data?.content_b64) return;
    let cancelled = false;
    setPdfLoading(true);
    (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        const bytes = Uint8Array.from(atob(data.content_b64), c => c.charCodeAt(0));
        const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
        const rendered = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width; canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          rendered.push({ canvas, pageNum: i });
        }
        if (!cancelled) { setPages(rendered); setPdfLoading(false); }
      } catch { if (!cancelled) setPdfLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [data]);

  // Build a map of field values from every party plus the sender
  const valueMap = {};
  (data?.parties || []).forEach(p => (p.field_values || []).forEach(fv => { valueMap[fv.id] = fv.value; }));
  (data?.sender_field_values || []).forEach(fv => { valueMap[fv.id] = fv.value; });

  const STATUS_LABELS = {
    pending: 'Awaiting Signature',
    partially_signed: 'In Progress',
    awaiting_execution: 'Awaiting Sender',
    executed: 'Fully Executed',
    declined: 'Declined',
  };
  const statusColor = data?.status === 'executed' ? '#10B981'
    : data?.status === 'awaiting_execution' ? '#3B82F6'
    : '#F59E0B';

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
        <div>
          <h2 className="font-semibold text-[#1E2A33] text-base">{data?.document_title || 'Signed Document'}</h2>
          <div className="flex items-center gap-3 mt-0.5">
            {data && (
              <>
                <span className="text-xs text-[#5C6670]">
                  {(data.parties || []).map(p => p.name || p.email).join(', ')}
                </span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                  style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}44` }}>
                  {STATUS_LABELS[data.status] || data.status}
                </span>
              </>
            )}
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-[#5C6670]">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
        {loading || pdfLoading ? (
          <div className="flex items-center justify-center h-full gap-2 text-[#5C6670]">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading document…
          </div>
        ) : (
          <div className="flex flex-col items-center gap-6 max-w-3xl mx-auto">
            {pages.map(pg => {
              const pageFields = (data.fields || []).filter(f => f.page === pg.pageNum);
              return (
                <div key={pg.pageNum} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', width: '100%' }}>
                  <canvas
                    ref={el => { if (el) { el.width = pg.canvas.width; el.height = pg.canvas.height; el.getContext('2d').drawImage(pg.canvas, 0, 0); } }}
                    style={{ display: 'block', width: '100%', height: 'auto' }}
                  />
                  {pageFields.map(f => {
                    const value = f.role === 'prefill' ? f.value : valueMap[f.id];
                    const color = f.role === 'sender' ? '#F97316' : f.role === 'prefill' ? '#F59E0B' : '#A62B1F';
                    const w = f.w_pct || (f.type === 'signature' ? 28 : f.type === 'date' ? 18 : 28);
                    const h = f.h_pct || (f.type === 'signature' ? 8 : 5);
                    return (
                      <div key={f.id} style={{
                        position: 'absolute',
                        left: `${f.x_pct}%`, top: `${f.y_pct}%`,
                        width: `${w}%`, height: `${h}%`,
                        border: value ? 'none' : '1px dashed #E3DDD1',
                        background: 'transparent',
                        borderRadius: 3,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden',
                      }}>
                        {f.type === 'signature' && value ? (
                          <img src={value} alt="sig" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                          <span style={{ fontSize: 11, color: value ? '#1E2A33' : '#9AA3AB', padding: '0 4px', textAlign: 'center' }}>
                            {value || '—'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* Audit trail */}
        {data && (
          <div className="max-w-3xl mx-auto mt-4">
            <div style={{ background: '#FBF8F2', borderRadius: 8, border: '1px solid #E3DDD1', padding: '14px 18px' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#9AA3AB', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Audit Trail</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <p style={{ fontSize: 12, color: '#5C6670' }}>📄 <strong style={{ color: '#1E2A33' }}>{data.document_title}</strong></p>
                {(data.parties || []).map((p, i) => (
                  <p key={p.id || i} style={{ fontSize: 12, color: '#5C6670' }}>
                    {p.status === 'not_required' ? (
                      <span style={{ color: '#9AA3AB' }}>➖ <span style={{ textDecoration: 'line-through' }}>{p.name || p.email}</span> · not required{p.not_required_by ? ` (released by ${p.not_required_by}, ${new Date(p.not_required_at).toLocaleString()})` : ''}</span>
                    ) : (<>
                    ✍ {p.role === 'reviewer' ? 'Reviewed' : 'Signed'} by <strong style={{ color: '#1E2A33' }}>{p.name || p.email}</strong>
                    {p.signed_at ? ` · ${new Date(p.signed_at).toLocaleString()}` : ' · pending'}
                    {p.signed_ip ? ` · IP ${p.signed_ip}` : ''}
                    </>)}
                  </p>
                ))}
                {data.executed_at && <p style={{ fontSize: 12, color: '#5C6670' }}>✅ Executed by <strong style={{ color: '#1E2A33' }}>{data.executed_by || data.executor_email || data.sent_by}</strong> · {new Date(data.executed_at).toLocaleString()}</p>}
                {data.document_hash && <p style={{ fontSize: 11, color: '#9AA3AB', fontFamily: 'monospace' }}>Document hash: {data.document_hash.slice(0, 24)}…</p>}
                <p style={{ fontSize: 12, color: '#5C6670' }}>🔑 Reference: <strong style={{ color: '#1E2A33', fontFamily: 'monospace' }}>{submissionId?.slice(0, 8).toUpperCase()}</strong></p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
